import { apiGet, apiPatch, apiPost } from "./fetch";
import { getGlobalHeadersAsObject } from "./global-headers";
// The support session and a workflow session are the same machinery read the
// same way, so the step and token shapes are shared rather than restated.
import type { WorkflowProgress } from "./workflow-tickets";

/** The collapsed, customer-facing status set — who the ball is with. */
/**
 * The staff work queue; reporters are never shown any of it, and none of it
 * decides what they can see — that is theirs, and it is closed_by_reporter_at.
 *
 * "new", "stalled" and "ended_by_reporter" are never stored: the backend
 * derives all three at read time, the last of them from the reporter's own
 * column, so the queue can report that they walked away without owning it.
 */
export type SupportTicketStatus =
	| "new"
	| "waiting_on_support"
	| "waiting_on_user"
	| "stalled"
	| "ended_by_reporter"
	| "resolved";

export const SUPPORT_STATUS_LABEL: Record<SupportTicketStatus, string> = {
	new: "New",
	waiting_on_support: "Waiting for support",
	waiting_on_user: "Waiting for customer",
	stalled: "Stalled",
	ended_by_reporter: "Ended by reporter",
	// Closed, not Solved: plenty of conversations end without anything being
	// solved, and a status that claims a fix for those is a lie in the queue.
	resolved: "Closed",
};

export const SUPPORT_STATUS_COLOR: Record<
	SupportTicketStatus,
	"info" | "warning" | "success" | "default"
> = {
	new: "info",
	waiting_on_support: "info",
	waiting_on_user: "warning",
	stalled: "default",
	ended_by_reporter: "default",
	resolved: "success",
};

/**
 * The queue: conversations where the ball is on our side. Deliberately an
 * inclusion — "not finished" would still drag in everything sitting with a
 * customer, which is precisely what nobody needs to look at.
 */
export const NEEDS_REPLY_STATUSES: SupportTicketStatus[] = [
	"new",
	"waiting_on_support",
];

/**
 * Finished as far as the queue is concerned. Nothing to do with the reporter,
 * who keeps every conversation until they close it themselves.
 */
export const FINISHED_SUPPORT_STATUSES: SupportTicketStatus[] = [
	"stalled",
	"ended_by_reporter",
	"resolved",
];

/**
 * What the queue is for: everything not finished. Derived from the one list
 * above rather than written out again, so a status added later cannot quietly
 * land outside both.
 */
export const OPEN_SUPPORT_STATUSES: SupportTicketStatus[] = (
	Object.keys(SUPPORT_STATUS_LABEL) as SupportTicketStatus[]
).filter((status) => !FINISHED_SUPPORT_STATUSES.includes(status));

/**
 * The statuses a human may set, which is not all of them.
 *
 * "New" and "Stalled" are derived at read time from the messages and the
 * clock, and the column has a check constraint forbidding them — offering
 * either in the dropdown sent staff a 500. Nothing would keep a stored one
 * true anyway: the first reply recomputes it.
 */
export const SETTABLE_SUPPORT_STATUSES: SupportTicketStatus[] = [
	"waiting_on_support",
	"waiting_on_user",
	"ended_by_reporter",
	"resolved",
];

export interface SupportAttachment {
	id: number;
	created: string;
	/** The picture's own shape, so its space can be held while it loads.
	 *  Null for anything uploaded before ODI started recording it. */
	width: number | null;
	height: number | null;
	message: number;
	caption: string;
	name: string;
	content_type: string;
}

export interface SupportMessage {
	id: number;
	created: string;
	ticket: number;
	author: number;
	author_name: string;
	is_from_reporter: boolean;
	body: string;
	/** Staff serializer only — the reporter never receives this. */
	context?: string;
	attachments: SupportAttachment[];
}

export type SupportAiSuggestionState = "pending" | "done";

export interface SupportAiSuggestion {
	id: number;
	created: string;
	updated: string;
	ticket: number;
	model: string;
	state: SupportAiSuggestionState;
	/** What the investigation found — addressed to the operator. */
	analysis: string;
	/** The reply ready to send — addressed to the customer. */
	text: string;
	requested_by: number | null;
	/**
	 * The Claude session that drafted it — superusers only, empty otherwise.
	 *
	 * The transcript is where a stalled draft is debugged, the same as for a
	 * workflow ticket, and it can hold whatever the investigation read out of
	 * production. So it is served to superusers and nobody else.
	 */
	cloud_session_url: string;
	/**
	 * What the session is doing right now — null once the draft has landed.
	 *
	 * Snapshotted by the dispatcher (only it holds the Claude credentials) and
	 * re-sent on every poll while a draft runs, which is what keeps the feed
	 * moving without reloading the page.
	 */
	progress: WorkflowProgress | null;
	/** What actually served the session when this draft landed. Stamped on the
	 *  row because the live snapshot expires; `model` above is what was ASKED
	 *  for, and a reused session answers on whatever it was created with. */
	session_model: string;
}

export type SupportResearchTurnState = "pending" | "done";

/** One follow-up question an operator put to the ticket's AI session, and its
 *  answer. Staff-only: the answer quotes production rows and log lines, so it
 *  is nested on the staff serializer and framed on the staff branch of the
 *  stream, and never reaches the reporter. */
export interface SupportResearchTurn {
	id: number;
	created: string;
	updated: string;
	ticket: number;
	/** Whose analysis — and whose cloud session — this question follows on from. */
	suggestion: number;
	question: string;
	/** Markdown, like the analysis. Empty while the session is still working. */
	answer: string;
	state: SupportResearchTurnState;
	asked_by: number | null;
	asked_by_name: string;
}

export interface SupportTicket {
	id: number;
	created: string;
	updated: string;
	title: string;
	/** Short name on the conversation's tab; empty until renamed. */
	label: string;
	status: SupportTicketStatus;
	/** The stored status plus what the clock says — what every view displays. */
	effective_status: SupportTicketStatus;
	reporter: number;
	reporter_seen_at: string | null;
	/** Set when the reporter closed it; null while it is still theirs. */
	closed_by_reporter_at: string | null;
	messages: SupportMessage[];
	// Staff serializer only — absent for the reporter.
	staff_seen_at?: string | null;
	workflow_ticket?: number | null;
	/** The linked development ticket's status, "" when there is none. An
	 *  abandoned one may be replaced; a live one may not. */
	workflow_ticket_status?: string;
	ai_suggestions?: SupportAiSuggestion[];
	research_turns?: SupportResearchTurn[];
	reporter_name?: string;
	reporter_group?: string;
}

interface PaginatedSupportTickets {
	results: SupportTicket[];
	count: number;
}

/**
 * The reporter's conversations, split by whether they have finished with them.
 *
 * `archived` is not optional here by accident: the panel draws a tab strip of
 * what is open and a list of what is not, and asking for both at once fetched
 * every conversation the person had ever had — with every message in each — on
 * every load of the app.
 *
 * `limit: 1` is how the archive tab gets its number before anyone opens it:
 * the paginator's `count` is the whole answer and one row is the cheapest way
 * to ask for it. Not 0 — DRF parses the limit strictly, so a zero is a
 * ValueError it swallows, falling back to the default page size of 1000.
 */
export function fetchSupportTickets(options?: {
	archived?: boolean;
	limit?: number;
	/**
	 * Only what I reported, never what I answer.
	 *
	 * Staff are two people here: the operator at /support who sees every
	 * conversation, and the ordinary user in the bubble who has their own.
	 * The bubble must ask for this or an operator's widget lists the whole
	 * queue as their own chats.
	 */
	mine?: boolean;
}): Promise<{ tickets: SupportTicket[]; total: number }> {
	const query = new URLSearchParams();
	if (options?.mine) query.set("mine", "true");
	// Omitted, not false: absent means unfiltered, which is what the staff
	// queue wants — it narrows by effective_status instead.
	if (options?.archived !== undefined)
		query.set("archived", String(options.archived));
	if (options?.limit !== undefined) query.set("limit", String(options.limit));
	return (
		apiGet(`/api/v2/support-tickets/?${query}`) as Promise<PaginatedSupportTickets>
	).then((payload) => ({ tickets: payload.results, total: payload.count }));
}

export function fetchSupportTicket(id: number): Promise<SupportTicket> {
	return apiGet(`/api/v2/support-tickets/${id}/`) as Promise<SupportTicket>;
}

/** Open a ticket: the title plus the thread's opening message. */
export function createSupportTicket(
	title: string,
	body: string,
	/** Browser context for staff; never shown back to the reporter. */
	context: string,
	/** How many pictures follow this message — see `pictures` on reply. */
	pictures: number
): Promise<SupportTicket> {
	return apiPost("/api/v2/support-tickets/", {
		title,
		body,
		context,
		pictures,
	}) as Promise<SupportTicket>;
}

export function renameSupportTicket(id: number, label: string): Promise<SupportTicket> {
	return apiPatch(`/api/v2/support-tickets/${id}/`, {
		label,
	}) as Promise<SupportTicket>;
}

/** The reporter marking a conversation done; writes a message and their own
 * closed_by_reporter_at. Writing in it again reopens it. */
export function closeSupportTicket(id: number): Promise<SupportTicket> {
	return apiPost(
		`/api/v2/support-tickets/${id}/close/`,
		{}
	) as Promise<SupportTicket>;
}

export function replySupportTicket(
	id: number,
	body: string,
	/**
	 * How many pictures this message is about to upload. They cannot ride
	 * with it — an attachment hangs off a message, so it takes a request of
	 * its own — and the AI draft is fired by this request, so it is told the
	 * count and waits rather than reading the thread in between.
	 */
	pictures: number
): Promise<SupportMessage> {
	return apiPost(`/api/v2/support-tickets/${id}/reply/`, {
		body,
		pictures,
	}) as Promise<SupportMessage>;
}

/** Move my side's watermark to now — opening the thread calls this. */
export function markSupportTicketSeen(id: number): Promise<{ status: string }> {
	return apiPost(`/api/v2/support-tickets/${id}/mark_seen/`, {}) as Promise<{
		status: string;
	}>;
}

/** Tickets carrying unseen writing from the other side — the menu badge.
 *
 *  `mine` picks which badge: the bubble counts replies to the reader's own
 *  reports, the operator's counts the queue. Without it a staff member's
 *  bubble showed the queue's unread count. */
export function fetchSupportUnreadCount(options?: { mine?: boolean }): Promise<number> {
	const query = options?.mine ? "?mine=true" : "";
	return (
		apiGet(`/api/v2/support-tickets/unread_count/${query}`) as Promise<{
			count: number;
		}>
	).then((payload) => payload.count);
}

export function setSupportTicketStatus(
	id: number,
	status: SupportTicketStatus
): Promise<SupportTicket> {
	return apiPost(`/api/v2/support-tickets/${id}/set_status/`, {
		status,
	}) as Promise<SupportTicket>;
}

/** Hand the ticket to development — thread and pictures travel, no retyping. */
export function exportSupportTicketToWorkflow(id: number): Promise<SupportTicket> {
	return apiPost(
		`/api/v2/support-tickets/${id}/export_to_workflow/`,
		{}
	) as Promise<SupportTicket>;
}

/** Staff ask for a fresh AI draft with a chosen model. */
export function requestSupportSuggestion(
	id: number,
	model: string
): Promise<SupportAiSuggestion> {
	return apiPost(`/api/v2/support-tickets/${id}/suggest_reply/`, {
		model,
	}) as Promise<SupportAiSuggestion>;
}

/**
 * Ask the ticket's AI session one more question.
 *
 * Rides the newest finished analysis's session, which still holds the ticket,
 * the code and its read-only production access — so a follow-up costs a turn
 * rather than an investigation. Rejects (400) while an earlier question is
 * still being answered: two turns in one session interleave.
 */
export function askSupportResearch(
	id: number,
	question: string
): Promise<SupportResearchTurn> {
	return apiPost(`/api/v2/support-tickets/${id}/research/`, {
		question,
	}) as Promise<SupportResearchTurn>;
}

export function uploadSupportAttachment(
	messageId: number,
	file: File,
	caption: string
): Promise<SupportAttachment> {
	const data = new FormData();
	data.append("file", file);
	data.append("message", messageId.toString());
	data.append("name", file.name);
	data.append("caption", caption);
	// no explicit Content-Type: the browser sets the multipart boundary
	return fetch("/api/v2/support-ticket-attachment/", {
		method: "POST",
		headers: { ...getGlobalHeadersAsObject() },
		body: data,
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`HTTP status code: ${res.status}`);
		}
		return res.json() as Promise<SupportAttachment>;
	});
}

/** The authenticated endpoint streaming one picture's bytes. */
export function supportAttachmentUrl(id: number): string {
	return `/api/v2/support-ticket-attachment/${id}/download/`;
}
